import React, { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { Provider } from "react-redux";
import muiTheme from "./style/muiTheme";
import { CookiesProvider } from "react-cookie";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import ErrorView from "@/common/components/views/ErrorView";
import { HelmetProvider } from "react-helmet-async";
import appStore from "@/store/appStore";
import { version } from "@/version";
import { router } from "./router";
import { requestCsrfCookie } from "@/common/api/requests";
import { fetchAppMeta, fetchPrograms, initializeCurrentUserData } from "@/store/actions";
import BootstrapGate from "@/common/components/layout/BootstrapGate";
Bugsnag.start({
  // @see also common/lib/errorHandler.js
  apiKey: import.meta.env.VITE_BUGSNAG_KEY,
  plugins: [new BugsnagPluginReact()],
  enabledReleaseStages: ["production", "staging"],
  releaseStage: import.meta.env.VITE_BUGSNAG_STAGE,
  appVersion: version,
});

const ErrorBoundary = Bugsnag.getPlugin("react")!.createErrorBoundary(React);
const container = document.getElementById("root")!;
const root = createRoot(container, {
  // React 19 no longer re-throws caught errors to window.onerror.
  // Forward them to Bugsnag so they're still reported.
  onCaughtError(error) {
    Bugsnag.notify(error instanceof Error ? error : new Error(String(error)));
  },
  onUncaughtError(error) {
    Bugsnag.notify(error instanceof Error ? error : new Error(String(error)));
  },
});

// Kick off bootstrap before routing. BootstrapGate below prevents
// the router from rendering until currentUser is bootstrapped.
requestCsrfCookie().then(() => {
  appStore.dispatch(fetchPrograms());
  appStore.dispatch(fetchAppMeta());
  appStore.dispatch(initializeCurrentUserData());
});

// Sync Redux auth state to router context.
// Only updates when currentUser reference changes, so unrelated
// Redux updates (appMeta, programs, etc.) don't trigger expensive
// route tree re-evaluation.
let prevCurrentUser = appStore.getState().auth.currentUser;
router.update({
  context: { currentUser: prevCurrentUser },
});
appStore.subscribe(() => {
  const nextCurrentUser = appStore.getState().auth.currentUser;
  if (nextCurrentUser !== prevCurrentUser) {
    prevCurrentUser = nextCurrentUser;
    router.update({
      context: { currentUser: nextCurrentUser },
    });
  }
});

root.render(
  <StrictMode>
    <ErrorBoundary FallbackComponent={ErrorView}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={muiTheme}>
          <HelmetProvider>
            <Provider store={appStore}>
              <CookiesProvider>
                <BootstrapGate>
                  <RouterProvider router={router} />
                </BootstrapGate>
              </CookiesProvider>
            </Provider>
          </HelmetProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </ErrorBoundary>
  </StrictMode>
);
